/**
 * @file index.jsx
 * @brief Renders the moderation dashboard for reviewing listings and browsing users.
 */
import { useState, useMemo } from "react";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createApiClient } from "../../api/axios";
import useModerationData from "../../hooks/useModerationData";
import useModerationOverview from "../../hooks/useModerationOverview";
import { truncateText } from "../../utils/truncateText";
import styles from "./moderation.module.css";

/* ─── Status config ──────────────────────────────────────────────────────── */

const STATUS_CONFIG = {
    pend_app: { key: "moderation.statuses.pending", cls: "statusPending" },
    active:   { key: "moderation.statuses.active", cls: "statusActive"  },
    rejected: { key: "moderation.statuses.rejected", cls: "statusRejected" },
};

/* ─── Component ──────────────────────────────────────────────────────────── */

/**
 * Displays moderation queues, bulk actions, and the user browser for moderators and admins.
 * @returns {JSX.Element} The moderation dashboard.
 */
export default function Moderation() {
    const auth = useAuth();
    const { t } = useTranslation();
    const accessToken = auth.user?.access_token;
    const isAuthenticated = auth.isAuthenticated;
    const api  = useMemo(
        () => createApiClient({
            isAuthenticated,
            user: accessToken ? { access_token: accessToken } : null,
        }),
        [accessToken, isAuthenticated]
    );

    const [activeTab, setActiveTab] = useState("overview"); // "overview" | "pending" | "rejected" | "users"
    const [search, setSearch]       = useState("");
    const [page, setPage]           = useState(1);
    const [chartMode, setChartMode] = useState("created"); // "created" | "snapshot"
    const [selectedListings, setSelectedListings] = useState([]);
    const { loading, count, next, previous, listings, users, imagesMap, updateListingStatus } = useModerationData({
        api,
        accessToken: auth.user?.access_token,
        activeTab,
        page,
        search,
    });
    const {
        loading: overviewLoading,
        error: overviewError,
        overview,
        refresh: refreshOverview,
    } = useModerationOverview({
        api,
        enabled: Boolean(accessToken),
    });

    /* ── Selection ── */
    const toggleSelect = (id) =>
        setSelectedListings((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );

    const allSelected = listings.length > 0 && listings.every((l) => selectedListings.includes(l.id));
    /**
        * Toggle selection for every listing shown on the current moderation page.
        * @returns {void}
     */
    const toggleSelectAll = () => {
        if (allSelected) setSelectedListings((p) => p.filter((id) => !listings.some((l) => l.id === id)));
        else             setSelectedListings((p) => [...new Set([...p, ...listings.map((l) => l.id)])]);
    };

    /* ── Status update ── */
    const emitModerationQueueDelta = (delta) => {
        if (typeof window === "undefined") return;
        if (!Number.isFinite(delta) || delta === 0) return;

        window.dispatchEvent(new CustomEvent("moderation:queue-delta", {
            detail: { delta },
        }));
    };

    const updateStatus = async (ids, status) => {
        try {
            await updateListingStatus(ids, status);

            // Pending queue shrinks immediately when a moderator approves/rejects pending listings.
            if (activeTab === "pending" && status !== "pend_app") {
                emitModerationQueueDelta(-ids.length);
            }

            setSelectedListings([]);
        } catch (err) {
            console.error("Status update failed", err);
        }
    };

    const switchTab = (tab) => { setActiveTab(tab); setPage(1); setSearch(""); setSelectedListings([]); };
    const totalPages = Math.max(1, Math.ceil(count / 10));
    const isListingTab = activeTab === "pending" || activeTab === "rejected";
    const itemCount = isListingTab ? listings.length : users.length;
    const selectedCount = selectedListings.length;
    const isOverviewTab = activeTab === "overview";

    const temporalChart = useMemo(() => {
        const basePoints = overview.temporalSeries || [];
        const points = chartMode === "snapshot"
            ? (() => {
                let running = Number(overview.activeBeforeWindow || 0);
                return basePoints.map((point) => {
                    running += Number(point.active || 0);
                    return {
                        ...point,
                        active: running,
                    };
                });
            })()
            : basePoints;
        const width = 620;
        const height = 220;
        const padding = { top: 20, right: 20, bottom: 34, left: 30 };
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;

        const maxY = Math.max(
            1,
            ...points.map((point) => point.active || 0)
        );

        const toPath = (key) => points.map((point, index) => {
            const x = padding.left + (index / Math.max(1, points.length - 1)) * chartWidth;
            const y = padding.top + chartHeight - ((point[key] || 0) / maxY) * chartHeight;
            return `${index === 0 ? "M" : "L"}${x} ${y}`;
        }).join(" ");

        return {
            width,
            height,
            maxY,
            points,
            yTicks: [0, Math.ceil(maxY / 2), maxY],
            activePath: toPath("active"),
            labelEvery: Math.max(1, Math.floor(points.length / 7)),
        };
    }, [chartMode, overview.activeBeforeWindow, overview.temporalSeries]);

    /* ── Render ── */
    return (
        <div className={styles.container}>

            <div className={styles.dashboardHeader}>
                <div>
                    <h2 className={styles.pageTitle}>🛡 {t("moderation.title")}</h2>
                    <p className={styles.subtitle}>
                        {isOverviewTab
                            ? t("moderation.overview.subtitle")
                            : t("common.resultCount", { count })}
                    </p>
                </div>
            </div>

            <div className={styles.topControls}>
                <div className={styles.tabs}>
                    {[
                        { key: "overview", label: t("moderation.overview.tab"), icon: "📊" },
                        { key: "pending", label: t("moderation.pending"), icon: "⏳" },
                        { key: "rejected", label: t("moderation.rejected"), icon: "✕" },
                        { key: "users", label: t("moderation.users"), icon: "👤" },
                    ].map(({ key, label, icon }) => (
                        <button
                            key={key}
                            className={`${styles.tab} ${activeTab === key ? styles.tabActive : ""}`}
                            onClick={() => switchTab(key)}
                        >
                            <span>{icon}</span> {label}
                        </button>
                    ))}
                </div>

                {!isOverviewTab ? (
                    <div className={styles.toolbar}>
                        <div className={styles.searchBox}>
                            <span className={styles.searchIcon}>🔎</span>
                            <input
                                className={styles.searchInput}
                                placeholder={activeTab === "users" ? t("moderation.searchUsers") : t("moderation.searchListings")}
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>
                    </div>
                ) : (
                    <button className={styles.overviewRefresh} onClick={refreshOverview}>
                        {t("moderation.overview.refresh")}
                    </button>
                )}
            </div>

            {activeTab === "pending" && selectedCount > 0 && (
                <div className={styles.bulkBar}>
                    <span className={styles.bulkCount}>{t("common.selectedCount", { count: selectedCount })}</span>
                    <button className={styles.btnApprove} onClick={() => updateStatus(selectedListings, "active")}>
                        ✓ {t("moderation.approveAll")}
                    </button>
                    <button className={styles.btnReject} onClick={() => updateStatus(selectedListings, "rejected")}>
                        ✕ {t("moderation.rejectAll")}
                    </button>
                    <button className={styles.bulkClear} onClick={() => setSelectedListings([])}>{t("moderation.clearSelection")}</button>
                </div>
            )}

            <section className={styles.contentPanel}>
                    {activeTab === "pending" && listings.length > 0 && !loading && (
                        <div className={styles.selectAllRow}>
                            <label className={styles.selectAllLabel}>
                                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                                {t("moderation.selectAllOnPage")}
                            </label>
                        </div>
                    )}

                    {isOverviewTab ? (
                        overviewLoading ? (
                            <div className={styles.skeletonGrid}>
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className={styles.skeletonCard}>
                                        <div className={styles.skeletonImg} />
                                        <div className={styles.skeletonBody}>
                                            <div className={styles.skeletonLine} style={{ width: "60%" }} />
                                            <div className={styles.skeletonLine} style={{ width: "36%" }} />
                                            <div className={styles.skeletonLine} style={{ width: "78%" }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : overviewError ? (
                            <div className={styles.empty}>
                                <span>⚠️</span>
                                <p>{t("moderation.overview.loadError")}</p>
                            </div>
                        ) : (
                            <div className={styles.overviewGrid}>
                                <article className={styles.metricCard}>
                                    <span className={styles.metricLabel}>{t("moderation.overview.pendingListings")}</span>
                                    <strong className={styles.metricValue}>{overview.pendingCount}</strong>
                                    <button className={styles.metricAction} onClick={() => switchTab("pending")}>
                                        {t("moderation.overview.openQueue")}
                                    </button>
                                </article>
                                <article className={styles.metricCard}>
                                    <span className={styles.metricLabel}>{t("moderation.overview.rejectedListings")}</span>
                                    <strong className={styles.metricValue}>{overview.rejectedCount}</strong>
                                    <button className={styles.metricAction} onClick={() => switchTab("rejected")}>
                                        {t("moderation.overview.reviewRejected")}
                                    </button>
                                </article>
                                <article className={styles.metricCard}>
                                    <span className={styles.metricLabel}>{t("moderation.overview.activeListings")}</span>
                                    <strong className={styles.metricValue}>{overview.activeCount}</strong>
                                    <span className={styles.metricMeta}>{t("moderation.overview.liveMarketplace")}</span>
                                </article>
                                <article className={styles.metricCard}>
                                    <span className={styles.metricLabel}>{t("moderation.overview.users")}</span>
                                    <strong className={styles.metricValue}>{overview.usersCount}</strong>
                                    <button className={styles.metricAction} onClick={() => switchTab("users")}>
                                        {t("moderation.overview.browseUsers")}
                                    </button>
                                </article>

                                <section className={styles.overviewPanel}>
                                    <h3 className={styles.overviewPanelTitle}>{t("moderation.overview.temporalGraphTitle")}</h3>
                                    <p className={styles.temporalSubtitle}>
                                        {t(
                                            chartMode === "snapshot"
                                                ? "moderation.overview.temporalGraphSnapshotHint"
                                                : "moderation.overview.temporalGraphCreatedHint",
                                            {
                                            days: overview.temporalDays || 14,
                                            }
                                        )}
                                    </p>
                                    <div className={styles.chartModeSwitch}>
                                        <button
                                            type="button"
                                            className={`${styles.chartModeBtn} ${chartMode === "created" ? styles.chartModeBtnActive : ""}`}
                                            onClick={() => setChartMode("created")}
                                        >
                                            {t("moderation.overview.modeCreated")}
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.chartModeBtn} ${chartMode === "snapshot" ? styles.chartModeBtnActive : ""}`}
                                            onClick={() => setChartMode("snapshot")}
                                        >
                                            {t("moderation.overview.modeSnapshot")}
                                        </button>
                                    </div>
                                    <div className={styles.temporalChartWrap}>
                                        <svg
                                            viewBox={`0 0 ${temporalChart.width} ${temporalChart.height}`}
                                            className={styles.temporalChart}
                                            role="img"
                                            aria-label={t(
                                                chartMode === "snapshot"
                                                    ? "moderation.overview.temporalGraphSnapshotAria"
                                                    : "moderation.overview.temporalGraphCreatedAria"
                                            )}
                                        >
                                            {temporalChart.yTicks.map((tick) => {
                                                const y = 20 + (temporalChart.height - 54) - (tick / temporalChart.maxY) * (temporalChart.height - 54);
                                                return (
                                                    <g key={tick}>
                                                        <line
                                                            x1="30"
                                                            x2={temporalChart.width - 20}
                                                            y1={y}
                                                            y2={y}
                                                            className={styles.temporalGridLine}
                                                        />
                                                        <text x="8" y={y + 4} className={styles.temporalAxisLabel}>{tick}</text>
                                                    </g>
                                                );
                                            })}

                                            <path d={temporalChart.activePath} className={styles.temporalLineActive} />

                                            {temporalChart.points.map((point, index) => {
                                                if (index % temporalChart.labelEvery !== 0 && index !== temporalChart.points.length - 1) {
                                                    return null;
                                                }

                                                const x = 30 + (index / Math.max(1, temporalChart.points.length - 1)) * (temporalChart.width - 50);
                                                const label = new Date(point.date).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });
                                                return (
                                                    <text key={`${point.date}-label`} x={x} y={temporalChart.height - 8} className={styles.temporalAxisLabel}>
                                                        {label}
                                                    </text>
                                                );
                                            })}
                                        </svg>
                                    </div>
                                    <div className={styles.temporalLegend}>
                                        <span className={styles.temporalLegendItem}>
                                            <span className={`${styles.legendDot} ${styles.legendDotActive}`} />
                                            {t("moderation.statuses.active")}
                                        </span>
                                    </div>
                                </section>

                                <section className={styles.overviewPanel}>
                                    <h3 className={styles.overviewPanelTitle}>{t("moderation.overview.recentPending")}</h3>
                                    {overview.recentPending.length === 0 ? (
                                        <p className={styles.overviewEmpty}>{t("moderation.overview.noPendingRecent")}</p>
                                    ) : (
                                        <div className={styles.overviewList}>
                                            {overview.recentPending.map((listing) => (
                                                <Link key={listing.id} to={`/listings/${listing.id}`} className={styles.overviewItemLink}>
                                                    <span className={styles.overviewItemTitle}>{truncateText(listing.title)}</span>
                                                    <span className={styles.overviewItemMeta}>{listing.price} €</span>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </section>
                            </div>
                        )
                    ) : loading ? (
                        <div className={styles.skeletonGrid}>
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className={styles.skeletonCard}>
                                    <div className={styles.skeletonImg} />
                                    <div className={styles.skeletonBody}>
                                        <div className={styles.skeletonLine} style={{ width: "50%" }} />
                                        <div className={styles.skeletonLine} style={{ width: "30%" }} />
                                        <div className={styles.skeletonLine} style={{ width: "70%" }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : isListingTab ? (
                        listings.length === 0 ? (
                            <div className={styles.empty}><span>🗂</span><p>{t("moderation.noListings")}</p></div>
                        ) : (
                            <div className={styles.listings}>
                                {listings.map((listing) => {
                                    const isSelected = selectedListings.includes(listing.id);
                                    return (
                                        <div key={listing.id} className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}>

                                    {/* Checkbox */}
                                    {activeTab === "pending" && (
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                className={styles.checkbox}
                                                checked={isSelected}
                                                onChange={() => toggleSelect(listing.id)}
                                            />
                                        </label>
                                    )}

                                    {/* Image */}
                                    <div className={styles.image}>
                                        {imagesMap[listing.id] ? (
                                            <img src={imagesMap[listing.id]} alt={listing.title} />
                                        ) : (
                                            <div className={styles.placeholder}>{t("common.noImage")}</div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className={styles.content}>
                                        <div className={styles.titleRow}>
                                            <h3 className={styles.titleListing} title={listing.title}>{truncateText(listing.title)}</h3>
                                            <span className={`${styles.statusBadge} ${styles[STATUS_CONFIG[listing.status]?.cls]}`}>
                                                {STATUS_CONFIG[listing.status]?.key ? t(STATUS_CONFIG[listing.status].key) : ""}
                                            </span>
                                        </div>

                                        <Link to={`/profile/${listing.author.id}`} className={styles.authorLink}>
                                            👤 {listing.author.user.first_name} {listing.author.user.last_name}
                                        </Link>

                                        <div className={styles.price}>{listing.price} €</div>

                                        {listing.description && (
                                            <p className={styles.description}>
                                                {listing.description.replace(/<[^>]+>/g, "").slice(0, 140)}
                                                {listing.description.length > 140 ? "…" : ""}
                                            </p>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className={styles.actions}>
                                        {activeTab === "pending" && (
                                            <>
                                                <button className={styles.btnApprove} onClick={() => updateStatus([listing.id], "active")}>
                                                    ✓ {t("moderation.approve")}
                                                </button>
                                                <button className={styles.btnReject} onClick={() => updateStatus([listing.id], "rejected")}>
                                                    ✕ {t("moderation.reject")}
                                                </button>
                                            </>
                                        )}
                                        {activeTab === "rejected" && (
                                            <button className={styles.btnApprove} onClick={() => updateStatus([listing.id], "active")}>
                                                ✓ {t("moderation.reapprove")}
                                            </button>
                                        )}
                                        <Link to={`/listings/${listing.id}/edit`} className={styles.btnEdit}>
                                            {t("common.edit")}
                                        </Link>
                                        <Link to={`/listings/${listing.id}`} className={styles.btnView}>
                                            {t("moderation.view")}
                                        </Link>
                                    </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        users.length === 0 ? (
                            <div className={styles.empty}><span>👤</span><p>{t("moderation.noUsers")}</p></div>
                        ) : (
                            <div className={styles.listings}>
                                {users.map((user) => (
                                    <div key={user.id} className={styles.card}>
                                        <div className={styles.userAvatar}>
                                            {(user.user.first_name?.[0] || user.user.username?.[0] || "U").toUpperCase()}
                                        </div>
                                        <div className={styles.content}>
                                            <h3 className={styles.titleListing}>
                                                {user.user.first_name} {user.user.last_name}
                                                <span className={styles.username}> @{user.user.username}</span>
                                            </h3>
                                            <p className={styles.userMeta}>📧 {user.user.email}</p>
                                            {user.groups?.length > 0 && (
                                                <div className={styles.groupTags}>
                                                    {user.groups.map((g) => (
                                                        <span key={g} className={styles.groupTag}>{g}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.actions}>
                                            <Link to={`/profile/${user.id}`} className={styles.btnView}>
                                                {t("moderation.viewProfile")}
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {itemCount > 0 && !loading && !isOverviewTab && (
                        <div className={styles.pagination}>
                            <button
                                className={`${styles.pageBtn} ${!previous ? styles.pageBtnDisabled : ""}`}
                                disabled={!previous}
                                onClick={() => setPage((p) => p - 1)}
                            >
                                ← {t("common.previous")}
                            </button>
                            <span className={styles.pageInfo}>{t("common.pageOf", { page, total: totalPages })}</span>
                            <button
                                className={`${styles.pageBtn} ${!next ? styles.pageBtnDisabled : ""}`}
                                disabled={!next}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                {t("common.next")} →
                            </button>
                        </div>
                    )}
            </section>
        </div>
    );
}
